import React, { useState } from "react";
import { Mail, Loader2, Check, Sparkles } from "lucide-react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * DigestSignup — email capture card used at the end of each blog post.
 *
 * Props:
 *  - source: analytics tag (route path of the post)
 *  - headline, subhead: optional overrides
 */
export default function DigestSignup({
  source,
  headline = "Join the Lunar Occultation digest.",
  subhead = "One short email every other week. Field notes on AI governance, from the engineers shipping it. No fluff, no AI-generated newsletters.",
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | ok | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("submitting");
    setError("");
    try {
      const r = await axios.post(`${API_BASE}/api/v1/subscribers`, {
        email,
        source: source || "unknown",
      });
      if (r.data?.subscribed) {
        setState("ok");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(
        Array.isArray(msg)
          ? "Please check the email address."
          : msg || "Could not subscribe. Try again in a moment."
      );
      setState("error");
    }
  };

  return (
    <section
      data-testid="digest-signup"
      className="mt-14 rounded-3xl p-8 md:p-10 bg-navy text-cream relative overflow-hidden"
    >
      <div className="absolute inset-0 starfield-soft opacity-70 pointer-events-none" />
      <div
        className="absolute -right-28 -top-28 w-[380px] h-[380px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,107,53,0.35) 0%, transparent 60%)", filter: "blur(18px)" }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 text-[#FFD98A] mb-3">
          <Sparkles size={14} />
          <span className="eyebrow" style={{ fontSize: 10 }}>THE LUNAR OCCULTATION DIGEST</span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight max-w-xl">{headline}</h3>
        <p className="text-cream/70 mt-3 max-w-xl text-sm md:text-base">{subhead}</p>

        {state === "ok" ? (
          <div data-testid="digest-signup-success" className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4 flex items-center gap-3 max-w-md">
            <div className="w-9 h-9 rounded-full bg-[#22c55e]/20 flex items-center justify-center">
              <Check size={16} className="text-[#22c55e]" />
            </div>
            <div>
              <div className="font-semibold text-cream text-sm">You're on the list.</div>
              <div className="text-cream/60 text-[12px]">The next digest hits your inbox in less than two weeks.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
            <div className="relative flex-1">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/40" />
              <input
                data-testid="digest-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-cream placeholder:text-cream/40 outline-none focus:ring-2 focus:ring-[#FF6B35]/50 text-sm"
                disabled={state === "submitting"}
              />
            </div>
            <button
              data-testid="digest-submit"
              type="submit"
              disabled={state === "submitting" || !email}
              className="btn-primary justify-center text-sm whitespace-nowrap disabled:opacity-60"
            >
              {state === "submitting" ? <><Loader2 size={14} className="animate-spin" /> Subscribing…</> : "Subscribe"}
            </button>
          </form>
        )}

        {state === "error" && (
          <div data-testid="digest-signup-error" className="mt-3 text-[12px] text-red-300 max-w-xl">
            {error}
          </div>
        )}

        <div className="mt-5 text-[11px] text-cream/40">
          No spam. Unsubscribe in one click. We do not sell or share your email.
        </div>
      </div>
    </section>
  );
}
